import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "currencyRadio", 
    "currencyInput", 
    "phoneInput", 
    "customAmount", 
    "amountRadio",
    "purchaseAmount", 
    "smsCredits", 
    "newBalance",
    "currentBalance",
    "phoneHelp",
    "amountContainer",
    "currencySymbol",
    "currencyCode"
  ]
  
  static values = {
    allCurrencies: Object
  }

  connect() {
    console.log("Currency selector controller connected")
    console.log("All currencies data:", this.allCurrenciesValue)
    this.updateFormForCurrentCurrency()
  }

  handleCurrencyChange(event) {
    const selectedCurrency = event.target.dataset.currency
    const currencyData = this.allCurrenciesValue[selectedCurrency]
    
    if (!currencyData) {
      console.error(`No data found for currency: ${selectedCurrency}`)
      return
    }

    console.log(`Currency changed to: ${selectedCurrency}`, currencyData)
    console.log('Full currency data:', currencyData)

    // Update hidden currency field - THIS IS CRITICAL!
    if (this.hasCurrencyInputTarget) {
      this.currencyInputTarget.value = selectedCurrency
      console.log(`Updated hidden currency field to: ${selectedCurrency}`)
    } else {
      console.error("Currency input target not found!")
    }

    // Update phone input placeholder and validation
    this.updatePhoneInput(currencyData)

    // Update suggested amounts
    this.updateSuggestedAmounts(currencyData)

    // Update current balance display
    this.updateCurrentBalance(currencyData)

    // Update currency symbols
    this.updateCurrencySymbols(currencyData)

    // Clear any selected amounts
    this.clearAmountSelection()

    // Update calculations with zero amount initially
    this.updateCalculations(currencyData, 0)
    
    console.log(`Form fully updated for currency: ${selectedCurrency}`)
  }

  updatePhoneInput(currencyData) {
    if (!this.hasPhoneInputTarget) {
      console.warn('Phone input target not found')
      return
    }

    const config = currencyData.config
    const phonePrefix = config.phone_prefix || config["phone_prefix"]
    const country = config.country || config["country"]
    
    console.log(`Updating phone input: ${phonePrefix} for ${country}`)
    
    // Update placeholder
    this.phoneInputTarget.placeholder = `${phonePrefix}700123456 (${country})`
    
    // Clear phone input if it doesn't match new prefix
    if (this.phoneInputTarget.value && !this.phoneInputTarget.value.startsWith(phonePrefix)) {
      this.phoneInputTarget.value = ""
    }

    // Update help text
    if (this.hasPhoneHelpTarget) {
      const helpText = this.phoneHelpTarget.querySelector('p:last-child')
      if (helpText) {
        helpText.textContent = `Enter your ${country} phone number starting with ${phonePrefix}. We'll validate it and send the payment request to your mobile money provider.`
      }
    }
  }

  updateSuggestedAmounts(currencyData) {
    if (!this.hasAmountContainerTarget) return

    const suggestedAmounts = currencyData.suggested_amounts
    const currency = currencyData.code
    const smsCost = currencyData.config.sms_cost || currencyData.config["sms_cost"]

    // Find all amount radio buttons
    const amountRadios = this.amountContainerTarget.querySelectorAll('input[name="amount"]')
    const amountLabels = this.amountContainerTarget.querySelectorAll('label')

    // Update each suggested amount button
    suggestedAmounts.forEach((amount, index) => {
      if (amountRadios[index] && amountLabels[index]) {
        // Update radio button value
        amountRadios[index].value = amount
        
        // Update the display
        const amountDisplay = amountLabels[index].querySelector('.text-lg')
        const smsDisplay = amountLabels[index].querySelector('.text-xs')
        
        if (amountDisplay) {
          amountDisplay.textContent = this.formatAmount(amount, currency)
        }
        
        if (smsDisplay) {
          const smsCount = Math.floor(amount / smsCost)
          smsDisplay.textContent = `≈ ${smsCount.toLocaleString()} SMS`
        }
      }
    })

    // Update custom amount input limits
    const customAmountInput = this.customAmountTarget
    if (customAmountInput) {
      const config = currencyData.config
      customAmountInput.min = config.minimum_purchase || config["minimum_purchase"]
      customAmountInput.max = config.maximum_purchase || config["maximum_purchase"]
      customAmountInput.step = (config.decimal_places || config["decimal_places"]) === 0 ? 1 : 0.01
      
      // Update the range text
      const rangeText = customAmountInput.closest('div').querySelector('p')
      if (rangeText) {
        const min = this.formatAmount(customAmountInput.min, currency)
        const max = this.formatAmount(customAmountInput.max, currency)
        rangeText.innerHTML = `<span class="inline-flex items-center"><span class="font-medium">Range:</span><span class="ml-1">${min}</span><span class="mx-1">-</span><span>${max}</span></span>`
      }
    }
  }

  updateCurrentBalance(currencyData) {
    if (!this.hasCurrentBalanceTarget) return

    const balance = currencyData.current_balance
    const currency = currencyData.code
    
    this.currentBalanceTarget.textContent = this.formatAmount(balance, currency)
  }

  updateCurrencySymbols(currencyData) {
    const config = currencyData.config
    const currency = currencyData.code
    const symbol = config.symbol || config["symbol"] || currency

    // Update currency symbol in custom amount field
    if (this.hasCurrencySymbolTarget) {
      this.currencySymbolTarget.textContent = symbol
    }

    // Update currency code display
    if (this.hasCurrencyCodeTarget) {
      this.currencyCodeTarget.textContent = currency
    }
  }

  clearAmountSelection() {
    // Clear radio button selections
    this.amountRadioTargets.forEach(radio => radio.checked = false)
    
    // Clear custom amount
    if (this.hasCustomAmountTarget) {
      this.customAmountTarget.value = ""
    }
  }

  // Handle amount selection changes
  handleAmountChange(event) {
    const selectedCurrency = this.currencyInputTarget.value
    const currencyData = this.allCurrenciesValue[selectedCurrency]
    
    if (!currencyData) return

    let amount = 0
    
    if (event.target.name === "amount") {
      // Radio button selected
      amount = parseFloat(event.target.value) || 0
      // Clear custom amount when radio is selected
      if (this.hasCustomAmountTarget) {
        this.customAmountTarget.value = ""
      }
    } else if (event.target === this.customAmountTarget) {
      // Custom amount entered
      amount = parseFloat(event.target.value) || 0
      // Clear radio selections when custom amount is entered
      this.amountRadioTargets.forEach(radio => radio.checked = false)
    }

    this.updateCalculations(currencyData, amount)
  }

  updateCalculations(currencyData, amount) {
    const currency = currencyData.code
    const smsCost = currencyData.config.sms_cost || currencyData.config["sms_cost"]
    const currentBalance = currencyData.current_balance
    
    // Calculate SMS credits
    const smsCredits = amount > 0 ? Math.floor(amount / smsCost) : 0
    
    // Update displays
    if (this.hasPurchaseAmountTarget) {
      this.purchaseAmountTarget.textContent = this.formatAmount(amount, currency)
    }
    
    if (this.hasSmsCreditsTarget) {
      this.smsCreditsTarget.textContent = `≈ ${smsCredits.toLocaleString()} messages`
    }
    
    if (this.hasNewBalanceTarget) {
      const newBalance = currentBalance + amount
      this.newBalanceTarget.textContent = this.formatAmount(newBalance, currency)
    }
  }

  formatAmount(amount, currency) {
    const config = this.allCurrenciesValue[currency]?.config
    if (!config) return `${currency} ${amount}`

    const decimalPlaces = config.decimal_places || config["decimal_places"] || 0
    const symbol = config.symbol || config["symbol"] || currency
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    })
    
    return `${symbol} ${formatter.format(amount)}`
  }

  updateFormForCurrentCurrency() {
    const selectedRadio = document.querySelector('input[name="selected_currency"]:checked')
    if (selectedRadio) {
      this.handleCurrencyChange({ target: selectedRadio })
    } else {
      // Select the default currency if none is selected
      const defaultRadio = document.querySelector('input[name="selected_currency"]')
      if (defaultRadio) {
        defaultRadio.checked = true
        this.handleCurrencyChange({ target: defaultRadio })
      }
    }
  }

  // Handle phone input validation
  validatePhone() {
    const selectedCurrency = this.currencyInputTarget.value
    const currencyData = this.allCurrenciesValue[selectedCurrency]
    const phoneValue = this.phoneInputTarget.value
    
    if (!currencyData || !phoneValue) return

    const config = currencyData.config
    const phonePrefix = config.phone_prefix || config["phone_prefix"]
    
    // Simple validation - you can enhance this
    const isValid = phoneValue.startsWith(phonePrefix) && phoneValue.length >= 10
    
    // You can add visual feedback here
    console.log(`Phone validation for ${selectedCurrency}: ${isValid}`)
  }
}